import {WuCheckbox} from '@/components/checkbox'
import React, {useCallback, useMemo} from 'react'
import {IWuTableColumnDef} from '../types/IWuTableColumnDef'

interface IUseRowSelectionProps<T> {
  columns: IWuTableColumnDef<T>[]
  rowSelection?: {
    isEnabled: boolean
    onRowSelect: React.Dispatch<React.SetStateAction<T[]>>
    rowUniqueKey: string
  }
}

interface IUseRowSelectionResult<T> {
  finalColumns: IWuTableColumnDef<T>[]
}

export const useRowSelection = <T,>({
  columns,
  rowSelection,
}: IUseRowSelectionProps<T>): IUseRowSelectionResult<T> => {
  const isEnabled = rowSelection?.isEnabled ?? false
  const {onRowSelect, rowUniqueKey} = rowSelection || {}

  const handleRowSelectChange = useCallback(
    (row: T, value: boolean): void => {
      const uniqueKey = row[rowUniqueKey as keyof T] as string | number

      onRowSelect!((prevSelectedRows: T[]) => {
        return value
          ? [...prevSelectedRows, row]
          : prevSelectedRows.filter(
              r =>
                (r[rowUniqueKey as keyof T] as string | number) !== uniqueKey,
            )
      })
    },
    [onRowSelect, rowUniqueKey],
  )

  const getHandleSelectAllChange = useCallback(
    (currentPageData: T[]) => {
      return (value: boolean): void => {
        onRowSelect!(() => (value ? currentPageData : []))
      }
    },
    [onRowSelect],
  )

  const finalColumns = useMemo<
    IWuTableColumnDef<T>[]
  >((): IWuTableColumnDef<T>[] => {
    if (!isEnabled) {
      return columns
    }

    return [
      {
        id: 'select',
        header: ({table}): React.JSX.Element => {
          const currentPageData = table
            .getRowModel()
            .rows.map(row => row.original)
          const handleSelectAllChange =
            getHandleSelectAllChange(currentPageData)

          return (
            <WuCheckbox
              checked={
                table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()
              }
              partial={
                table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
              }
              onChange={value => {
                table.toggleAllRowsSelected(!!value)
                handleSelectAllChange(!!value)
              }}
              aria-label="Select all"
            />
          )
        },
        cell: ({row}): React.JSX.Element => {
          return (
            <WuCheckbox
              checked={row.getIsSelected()}
              onChange={value => {
                row.toggleSelected(!!value)
                handleRowSelectChange(row.original, value)
              }}
              aria-label="Select row"
            />
          )
        },
        enableSorting: false,
        filterable: false,
        accessorKey: 'select',
      },
      ...columns,
    ]
  }, [columns, isEnabled, getHandleSelectAllChange, handleRowSelectChange])

  return {
    finalColumns,
  }
}
